import { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HaveListRow } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { LANGUAGE_PILL_OPTIONS } from 'constants/languages';
import { useCanUseHaveListAlerts } from 'hooks/useCanUseHaveListAlerts';
import './BatchEditActionBar.css';
import { CardFinish, FINISH_LABELS, FINISH_ORDER, fromHaveFinish, getAvailableFinishes, HaveFinish } from 'utils/finishes';
import { FinishIcon } from 'components/FinishTag';

// Condition options shared between have and want list
const CONDITION_OPTIONS = [
  { value: '', label: '-- No change --' },
  { value: 'NEAR_MINT', label: 'NM' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP' },
  { value: 'MODERATELY_PLAYED', label: 'MP' },
  { value: 'HEAVILY_PLAYED', label: 'HP' },
];

// For want list: selecting a min condition generates the acceptable conditions array
// e.g., selecting LP means NM + LP are acceptable
const CONDITION_HIERARCHY = [
  'NEAR_MINT',
  'SLIGHTLY_PLAYED',
  'MODERATELY_PLAYED',
  'HEAVILY_PLAYED',
];

const FINISH_OPTIONS_HAVE: { value: '' | HaveFinish; label: string }[] = [
  { value: '', label: '-- No change --' },
  { value: 'NONFOIL', label: 'Non-foil' },
  { value: 'FOIL', label: 'Foil' },
  { value: 'ETCHED', label: 'Etched' },
];

const TRADE_OPTIONS = [
  { value: '', label: '-- No change --' },
  { value: 'true', label: 'For Trade' },
  { value: 'false', label: 'Not for Trade' },
];

const ALERT_OPTIONS = [
  { value: '', label: '-- No change --' },
  { value: 'true', label: 'On' },
  { value: 'false', label: 'Off' },
];

const LANGUAGE_OPTIONS = LANGUAGE_PILL_OPTIONS;

export interface HaveListBatchChanges {
  condition?: CardCondition;
  /** Authoritative; `foil` is sent alongside as the derived mirror through Track D */
  finish?: HaveFinish;
  foil?: boolean;
  forTrade?: boolean;
  alertEnabled?: boolean;
}

export interface WantListBatchChanges {
  acceptableConditions?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  acceptEtched?: boolean;
  acceptableLanguages?: string[];
}

interface Props {
  selectedCount: number;
  isHaveList: boolean;
  isWantList: boolean;
  selectedRows: HaveListRow[];
  isApplying: boolean;
  onApplyHaveList?: (changes: HaveListBatchChanges) => void;
  onApplyWantList?: (changes: WantListBatchChanges) => void;
  onCancel: () => void;
}

export const BatchEditActionBar = ({
  selectedCount,
  isHaveList,
  isWantList,
  selectedRows,
  isApplying,
  onApplyHaveList,
  onApplyWantList,
  onCancel,
}: Props) => {
  // Have list field state
  const [condition, setCondition] = useState('');
  const [foil, setFoil] = useState('');
  const [forTrade, setForTrade] = useState('');
  const [alertEnabled, setAlertEnabled] = useState('');

  const canUseAlerts = useCanUseHaveListAlerts();

  // Want list field state
  const [wantCondition, setWantCondition] = useState('');
  // Want finish SET (D-3 pills; the canned two-finish combinations cannot cover seven). Empty = no change.
  const [wantFinishes, setWantFinishes] = useState<CardFinish[]>([]);
  const [wantLanguages, setWantLanguages] = useState<string[]>([]);

  // Check if any changes have been made
  const hasChanges = isHaveList
    ? condition !== '' || foil !== '' || forTrade !== '' || alertEnabled !== ''
    : wantCondition !== '' || wantFinishes.length > 0 || wantLanguages.length > 0;

  // Have list: warn when a selected card does not exist in the target finish. The
  // backend stores the finish it does exist in and reports the adjustment, so this
  // is a heads-up, not a block.
  const finishWarning = useMemo(() => {
    if (!isHaveList || !foil) return null;
    const target = fromHaveFinish(foil as HaveFinish);
    const lacking = selectedRows.filter(row => !getAvailableFinishes(row.card).includes(target)).length;
    if (lacking === 0) return null;
    return `${lacking} of the selected cards ${lacking === 1 ? 'does' : 'do'} not exist in ${FINISH_LABELS[target]}; ${lacking === 1 ? 'it' : 'they'} will be stored in the finish ${lacking === 1 ? 'it does' : 'they do'} exist in.`;
  }, [isHaveList, foil, selectedRows]);

  // Want list: check for duplicate card warning
  const duplicateWarning = useMemo(() => {
    if (!isWantList || wantCondition === '') return null;

    // Group selected rows by oracleId
    const groups: Record<string, HaveListRow[]> = {};
    selectedRows.forEach(row => {
      const key = row.oracleId || row.cardName || '';
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    // Check if any group has rows with different conditions
    const groupKeys = Object.keys(groups);
    for (let i = 0; i < groupKeys.length; i++) {
      const groupRows = groups[groupKeys[i]];
      if (groupRows.length > 1) {
        const conditions = new Set(groupRows.map((r: HaveListRow) =>
          JSON.stringify(r.acceptableConditions || [])
        ));
        if (conditions.size > 1) {
          return 'Some selected cards have different conditions set. Applying a single condition will overwrite all of them.';
        }
      }
    }
    return null;
  }, [isWantList, wantCondition, selectedRows]);

  const handleApply = useCallback(() => {
    if (isHaveList && onApplyHaveList) {
      const changes: HaveListBatchChanges = {};
      if (condition) changes.condition = condition as CardCondition;
      if (foil) {
        changes.finish = foil as HaveFinish;
        changes.foil = foil !== 'NONFOIL';
      }
      if (forTrade) changes.forTrade = forTrade === 'true';
      if (alertEnabled) changes.alertEnabled = alertEnabled === 'true';
      onApplyHaveList(changes);
    }

    if (isWantList && onApplyWantList) {
      const changes: WantListBatchChanges = {};
      if (wantCondition) {
        // Generate acceptable conditions from min condition threshold
        const idx = CONDITION_HIERARCHY.indexOf(wantCondition);
        if (idx !== -1) {
          changes.acceptableConditions = CONDITION_HIERARCHY.slice(0, idx + 1);
        }
      }
      if (wantFinishes.length > 0) {
        changes.acceptNonFoil = wantFinishes.includes('nonfoil');
        changes.acceptFoil = wantFinishes.includes('foil');
        changes.acceptEtched = wantFinishes.includes('etched');
      }
      if (wantLanguages.length > 0) {
        changes.acceptableLanguages = wantLanguages;
      }
      onApplyWantList(changes);
    }
  }, [isHaveList, isWantList, condition, foil, forTrade, alertEnabled, wantCondition, wantFinishes, wantLanguages, onApplyHaveList, onApplyWantList]);

  const toggleWantFinish = useCallback((finish: CardFinish) => {
    setWantFinishes(prev =>
      prev.includes(finish) ? prev.filter(f => f !== finish) : FINISH_ORDER.filter(f => f === finish || prev.includes(f))
    );
  }, []);

  const toggleLanguage = useCallback((code: string) => {
    setWantLanguages(prev =>
      prev.includes(code)
        ? prev.filter(l => l !== code)
        : [...prev, code]
    );
  }, []);

  const bar = (
    <div className="batch-edit-overlay">
      <div className="batch-edit-bar">
        <div className="batch-edit-fields">
          {isHaveList && (
            <>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Condition</span>
                <select
                  className={`batch-edit-select ${condition ? 'changed' : ''}`}
                  value={condition}
                  onChange={e => setCondition(e.target.value)}
                  disabled={isApplying}
                >
                  {CONDITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Finish</span>
                <select
                  className={`batch-edit-select ${foil ? 'changed' : ''}`}
                  value={foil}
                  onChange={e => setFoil(e.target.value)}
                  disabled={isApplying}
                >
                  {FINISH_OPTIONS_HAVE.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Trade Status</span>
                <select
                  className={`batch-edit-select ${forTrade ? 'changed' : ''}`}
                  value={forTrade}
                  onChange={e => setForTrade(e.target.value)}
                  disabled={isApplying}
                >
                  {TRADE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {canUseAlerts && (
                <div className="batch-edit-field">
                  <span className="batch-edit-field-label">Alerts</span>
                  <select
                    className={`batch-edit-select ${alertEnabled ? 'changed' : ''}`}
                    value={alertEnabled}
                    onChange={e => setAlertEnabled(e.target.value)}
                    disabled={isApplying}
                  >
                    {ALERT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {isWantList && (
            <>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Min. Condition</span>
                <select
                  className={`batch-edit-select ${wantCondition ? 'changed' : ''}`}
                  value={wantCondition}
                  onChange={e => setWantCondition(e.target.value)}
                  disabled={isApplying}
                >
                  {CONDITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Finish</span>
                {/* Same pill geometry as the language pills (design pass); no pill = no change */}
                <div className="batch-edit-lang-pills" role="group" aria-label="Accepted finishes">
                  {FINISH_ORDER.map(finish => (
                    <button
                      key={finish}
                      type="button"
                      className={`batch-edit-lang-pill ${wantFinishes.includes(finish) ? 'active' : ''}`}
                      onClick={() => toggleWantFinish(finish)}
                      disabled={isApplying}
                      aria-pressed={wantFinishes.includes(finish)}
                    >
                      <FinishIcon finish={finish} size="tag" className="batch-edit-finish-icon" />
                      {FINISH_LABELS[finish]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="batch-edit-field">
                <span className="batch-edit-field-label">Language</span>
                <div className="batch-edit-lang-pills">
                  {LANGUAGE_OPTIONS.map(lang => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`batch-edit-lang-pill ${wantLanguages.includes(lang.code) ? 'active' : ''}`}
                      onClick={() => toggleLanguage(lang.code)}
                      disabled={isApplying}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {finishWarning && (
            <div className="batch-edit-warning" data-testid="batch-finish-warning">
              <FontAwesomeIcon icon="triangle-exclamation" />
              <span>{finishWarning}</span>
            </div>
          )}

          {duplicateWarning && (
            <div className="batch-edit-warning">
              <FontAwesomeIcon icon="triangle-exclamation" />
              <span>{duplicateWarning}</span>
            </div>
          )}
        </div>

        <div className="batch-edit-actions">
          <button
            className="batch-edit-apply-btn"
            onClick={handleApply}
            disabled={!hasChanges || isApplying}
          >
            {isApplying ? (
              <>
                <div className="batch-edit-spinner" />
                Applying...
              </>
            ) : (
              `Apply to ${selectedCount} card${selectedCount !== 1 ? 's' : ''}`
            )}
          </button>
          <button
            className="batch-edit-cancel-btn"
            onClick={onCancel}
            disabled={isApplying}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(bar, document.body);
};
